import React from 'react';
import { undoDateFormat, textfieldDateFormat } from "../../../utils/dateFunctions";
import { numberFormat } from "../../../utils/numberFunctions";
import { handlePagination, postApi } from "../../../utils/apiFunction";
import MUIDataTable from "mui-datatables";
import { getOysterGrades, gradeSort } from "../../../definitions/oysters";
import Link from "@mui/material/Link";
import IconButton from "@mui/material/IconButton";
import WebhookIcon from '@mui/icons-material/Webhook';
import EditIcon from '@mui/icons-material/Edit';
import { useJumboTheme } from "@jumbo/hooks";
import { CircularProgress, Typography } from "@mui/material";
import { TableCell, TableRow, TableFooter } from "@mui/material";
import { ThemeProvider } from '@mui/material/styles';
import { tableTheme } from '../../../themes/tableTheme';
import { ACCESS, ACCESS_SECTIONS } from '../../../utils/constants/access';
import { adalApiFetch, userInfo } from '../../../../adalConfig';
import DataTableStandard from "../../../components/DataTableStandard";
import { reverseSort } from '../../../utils/sortFunctions';
import { salesOrderAndShipmentsTypes, statusList } from '../../../utils/constants/selectFields';
import getCreditMemoData from '../Function/GetCreditMemoList';
import EditCMStock from './EditCreditMemoStock.js';
import { DatePickerAutoOpen } from '../../../shared/DatePicker';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import { SOstatusList } from '../../../utils/constants/selectFields';
import { SimpleStringSelect } from "../../../components/StringSelect";
import AddSalesOrder from '../Forms/AddSalesOrder';
import FormModal from '../../../components/FormModal';
import { checkAccess } from '../../../utils/roleFunctions';
import FormControlLabel from '@mui/material/FormControlLabel';
import DeleteIcon from '@mui/icons-material/Delete';
import useSwalWrapper from "@jumbo/vendors/sweetalert2/hooks";


const CreditMemoEditable = (props) => {
    const uri = process.env.REACT_APP_BASE_URL;
    const [user, setUser] = React.useState(userInfo());
    const [loading, setLoading] = React.useState(false);
    const [columns, setColumns] = React.useState([]);
    const [procurement, setProcurement] = React.useState([]);
    const [colourOverride, setColourOverride] = React.useState([]);
    const [localReload, setLocalReload] = React.useState(false);
    const [showPriceline, setShowPriceline] = React.useState(false);
    const [tableReload, setTableReload] = React.useState(false);
    const [editorRows, setEditorRows] = React.useState(new Set()); // track inline editor rows
    const { theme } = useJumboTheme();
    const Swal = useSwalWrapper();
    const allowEdit = checkAccess(props.access, { key: ACCESS_SECTIONS.CREDITMEMOS, value: ACCESS.EDIT });
    const allowApprove = checkAccess(props.access, { key: ACCESS_SECTIONS.CREDITMEMOS, value: ACCESS.MYOB });


    const deleteOrder = React.useCallback(async (dataIndex) => {
        const rowObj = procurement[dataIndex];
        const name = rowObj?.name ?? dataIndex;
        Swal.fire({
            html: 'Are you sure you want to delete this ' + name + ' credit memo?',
            icon: 'warning',
            title: 'Confirm Delete...',
            showCancelButton: true,
            confirmButtonText: 'Yes, Continue',
            cancelButtonText: 'No',

        }).then(async result => {
            if (result.value) {
                setLoading(true);
                const id = rowObj?.id;
                await postApi('/CreditMemo/Delete/' + id);
                triggerTableReload();
                setLoading(false);
            } else if (result.dismiss === Swal.DismissReason.cancel) {

            }
        });
    }, [procurement]);


    const toggleEditorRowByIndex = React.useCallback((dataIndex) => {
        const rowObj = procurement[dataIndex];
        const key = rowObj?.id ?? dataIndex;
        setEditorRows(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    }, [procurement]);

    const triggerReload = React.useCallback(() => {
        setLocalReload(r => !r);
    }, []);


    const triggerTableReload = React.useCallback(() => {
        setTableReload(r => !r);
    }, []);



    // Add: helper to change status then refresh
    const handleChangeStatus = React.useCallback(async (id, newStatus) => {
        setLoading(true);
        if (!id) return;
        await postApi('/CreditMemo/UpdateField/' + id, { User: user?.name, Status: newStatus });
        triggerTableReload();
        setLoading(false);
    }, [triggerTableReload]);

    React.useEffect(() => {
        var cols = [
            {
                name: 'myobid',
                label: 'Credit Memo',
                options: {
                    customBodyRenderLite: (dataIndex) => {
                        var tenant = process.env.REACT_APP_MYOB_TENANT ? process.env.REACT_APP_MYOB_TENANT : 'https://aoc.myobadvanced.com/Main?';
                        var proc = procurement;
                        if (proc[dataIndex]?.isInvoiceUpdate)
                            return <Link href={tenant + 'ScreenId=SO303000&DocType=INV&RefNbr=' + proc[dataIndex]?.myobid} target='_blank'>{proc[dataIndex]?.myobid}</Link>

                        else
                            return <Link href={tenant + 'ScreenId=SO301000&OrderType=CM&OrderNbr=' + proc[dataIndex]?.myobid} target='_blank'>{proc[dataIndex]?.myobid}</Link>
                    },
                    sortCompare: reverseSort
                }
            },
            {
                name: 'somyobid',
                label: 'Sales Order',
                options: {
                    customBodyRenderLite: (dataIndex) => {
                        var tenant = process.env.REACT_APP_MYOB_TENANT ? process.env.REACT_APP_MYOB_TENANT : 'https://aoc.myobadvanced.com/Main?';
                        var proc = procurement;
                        return <Link href={tenant + 'ScreenId=SO301000&OrderType=SO&OrderNbr=' + proc[dataIndex]?.somyobid} target='_blank'>{proc[dataIndex]?.somyobid}</Link>
                    },
                    sortCompare: reverseSort
                }
            },
            {
                name: 'effectiveDate',
                label: 'Effective Date',
            },
            {
                name: 'name',
                label: 'Customer',
                options: props.customerFilter ? { filterList: [props.customerFilter] } : {}
            },
            {
                name: 'synced',
                label: 'Synced'
            },
            {
                name: 'status',
                label: 'Status'
            },
            {
                name: 'description',
                label: 'Description'
            },
            {
                name: 'isReused',
                label: 'Stock Reused'
            },
            {
                name: 'editOrder',
                label: 'Edit Stock',
                options: {
                    customBodyRenderLite: (dataIndex) => {
                        return (
                            <IconButton
                                aria-label="edit"
                                onClick={(e) => { e.stopPropagation(); toggleEditorRowByIndex(dataIndex); }}
                                title="Edit stock inline"
                            >
                                <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        );
                    },
                }
            },
            {
                name: 'deleteOrder',
                label: 'Delete',
                options: {
                    customBodyRenderLite: (dataIndex) => {
                        return (
                            <IconButton
                                aria-label="delete"
                                onClick={(e) => { e.stopPropagation(); deleteOrder(dataIndex); }}
                                title="Delete orer"
                            >
                                <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        );
                    },
                }
            }
        ]

        setColumns(cols);
    }, [props.customerFilter, procurement, toggleEditorRowByIndex])

    const changeStatus = (index) => (newStatus) => {
        var statusOR = colourOverride.findIndex((col) => { return col.index === index });
        if (statusOR >= 0) {
            var newCol = [...colourOverride];
            newCol[statusOR].status = newStatus;
            setColourOverride(newCol);
        } else {
            setColourOverride([...colourOverride, { index: index, status: newStatus }]);
        }
    }

    const nextStatus = (currentStatus) => {
        if (currentStatus === "Select Stock") return "Awaiting Approval";
        if (currentStatus === "Awaiting Approval") return "Approved";
        return currentStatus;
    }

    const nextStatusLabel = (currentStatus) => {
        if (currentStatus === "Select Stock") return "Submit for Approval";
        if (currentStatus === "Awaiting Approval") return "Approve";
        return currentStatus;
    }

    const nextStatusVisible = (currentStatus) => {
        if (currentStatus === "Select Stock") return true;
        if (currentStatus === "Awaiting Approval") return true;
        return false;
    }

    const options = {
        filterType: 'dropdown',
        selectableRowsHideCheckboxes: true,
        fixedHeader: true,
        rowsPerPage: 15,
        responsive: 'standard',
        onRowClick: props.onClick ? props.onClick(procurement) : (() => { }),
        customRowRender: (data, dataIndex, rowIndex) => {
            var rowStyle = {
                background: getColour(data[5]),
            };
            var cellStyle = {
                fontSize: '12px',
                paddingTop: '3px',
                paddingBottom: '3px',
                whiteSpace: 'nowrap',
                maxWidth: '150px',
                overflow: 'hidden'

            };
            const rowObj = procurement[dataIndex];
            const rowKey = rowObj?.id ?? dataIndex;
            const isEditing = editorRows.has(rowKey);

            return (
                <React.Fragment key={rowKey}>
                    <TableRow style={rowStyle}>
                        {columns.map((coll, i) => {
                            if (coll.name === 'myobid' || coll.name === 'somyobid' || coll.name === 'editOrder') {
                                return <TableCell key={coll.name} style={cellStyle}>{data[i](dataIndex)}</TableCell>
                            }
                            else if (coll.name === 'deleteOrder' && (data[5] === ('Select Stock') || data[5] === ('Awaiting Approval'))) {
                                return <TableCell key={coll.name} style={cellStyle}>{data[i](dataIndex)}</TableCell>
                            }
                            else if (allowEdit && coll.name === 'description' && (data[5] === ('Select Stock') || data[5] === ('Awaiting Approval'))) {
                                return <TextTableCell key={coll.name} style={cellStyle} text={data[i]} id={rowObj?.id} onCommit={triggerReload} />
                            }
                            else if (allowEdit && coll.name === 'isReused' && (data[5] === ('Select Stock') || data[5] === ('Awaiting Approval'))) {
                                return <CheckBoxTableCell key={coll.name} style={cellStyle} checked={data[i] === 'Yes'} id={rowObj?.id} onCommit={triggerReload} />
                            }
                            return <TableCell key={coll.name} style={cellStyle}>{data[i]}</TableCell>
                        })}
                    </TableRow>

                    {isEditing && (
                        <TableRow>
                            <TableCell colSpan={columns.length} style={{ background: theme.palette.background.default, padding: 12 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Edit Stock for Credit Memo for SO: {rowObj?.somyobid ?? '-'}
                                </Typography>
                                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <EditCMStock id={rowObj?.id} status={data[5]} showPriceLine={showPriceline} />
                                    {/* New: Change Status button beside Close */}
                                    {
                                        loading && <CircularProgress size={20} />
                                    }
                                    {
                                        !loading && nextStatusVisible(rowObj?.status) && <>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                                onClick={(e) => { e.stopPropagation(); handleChangeStatus(rowObj?.id, nextStatus(rowObj?.status)); }}
                                                disabled={!allowEdit || (!allowApprove && rowObj?.status !== 'Select Stock')}
                                            >
                                                {nextStatusLabel(rowObj?.status)}
                                            </Button>
                                        </>
                                    }
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setEditorRows(prev => { const n = new Set(prev); n.delete(rowKey); return n; });
                                        }}
                                    >
                                        Close
                                    </Button>
                                    <span style={{ paddingLeft: '20px' }}>{rowObj?.createdBy || ''}</span>
                                    <span style={{ paddingLeft: '10px' }}>{rowObj?.approvedBy || ''}</span>

                                </div>
                            </TableCell>
                        </TableRow>
                    )}
                </React.Fragment>
            );
        }
    };

    const getColour = (selRow) => {
        if (selRow === ("Completed")) {
            return theme.palette.oysterProducts.completed
        } else if (selRow === ("Select Stock")) {
            return theme.palette.oysterProducts.onhold
        } else if (selRow === ("Approved")) {
            return theme.palette.oysterProducts.completed
        } else if (selRow === ("Pending Approval")) {
            return theme.palette.oysterProducts.completed
        } else if (selRow === ("Open")) {
            return theme.palette.oysterProducts.completed
        } else if (selRow === ("Balanced")) {
            return theme.palette.oysterProducts.completed
        } else if (selRow === ("Invoiced")) {
            return theme.palette.oysterProducts.completed
        } else if (selRow === ("Awaiting Approval")) {
            return 'rgba(152, 251, 152,0.3)'
        } else if (selRow === ("Canceled")) {
            return theme.palette.oysterProducts.cancelled
        } else if (selRow === ("Invoice Paid Error")) {
            return 'rgba(255, 0, 0, 0.5)'
        }
    }

    React.useEffect(() => {
        const abortController = new AbortController();
        setProcurement([]);
        setColourOverride([]);
        getCreditMemoData(textfieldDateFormat(props.startDate), textfieldDateFormat(props.endDate), abortController, setLoading, setProcurement);
        return () => {
            abortController.abort();
        };
    }, [props.startDate, props.endDate, props.reload, tableReload]);


    return (
        <DataTableStandard
            data={props.filter ? props.filter(procurement) : procurement}
            title={
                <Typography variant='h2'>
                    Credit Memos
                    <FormControlLabel
                        style={{ paddingLeft: '9px' }}
                        control={<Checkbox checked={showPriceline} onChange={(event) => { setShowPriceline(event.target.checked); triggerTableReload(); }} />}
                        label={"Show LotNbr. Price"}
                        disabled={props.disabled}
                        labelPlacement="left"
                    />
                    {/* Global loading indicator beside title */}
                    {loading && <CircularProgress size={20} style={{ marginLeft: 8, verticalAlign: 'middle' }} />}
                </Typography>
            }

            columns={columns}
            options={options}
            returnSearch={!loading}
            reload={props.reloadFunc}
            shrink={true}

        />
    );
};

export default CreditMemoEditable;

const TextTableCell = (props) => {
    const [selected, setSelected] = React.useState(false);
    const [text, setText] = React.useState(props.text);

    const updateValue = (newData) => {
        var params = {};
        params['Description'] = newData;
        params['User'] = userInfo()?.name;
        setText(newData);
        postApi('/CreditMemo/UpdateField/' + props.id, params);

    };

    const finishediting = () => {
        setSelected(false);
        if (typeof props.onCommit === 'function') props.onCommit();
    }

    if (selected) {
        return <TableCell {...props} style={{ maxWidth: '500px' }} > <TextField
            autoFocus
            disabled={props.disabled}
            id="desc"
            value={text ?? ''}
            onChange={(event) => { updateValue(event.target.value) }}
            multiline
            InputLabelProps={{
                shrink: true,
            }}
            rows={4}
            onBlur={() => finishediting()}
        />
        </TableCell>
    } else {
        return <TableCell {...props} onClick={() => { setSelected(true) }} >{text}</TableCell>
    }
}

const CheckBoxTableCell = (props) => {
    const [editing, setEditing] = React.useState(false);
    const [checked, setChecked] = React.useState(Boolean(props.checked));

    const updateValue = (newChecked) => {
        setChecked(newChecked);
        postApi('/CreditMemo/UpdateField/' + props.id, { User: userInfo()?.name, IsReused: newChecked });
        setEditing(false);
    };

    const finishediting = () => {
        setEditing(false);
        if (typeof props.onCommit === 'function') props.onCommit();
    }

    if (editing) {
        return (
            <TableCell {...props} style={{ maxWidth: '250px' }}>
                <Checkbox
                    autoFocus
                    checked={checked}
                    onChange={(event) => updateValue(event.target.checked)}
                    onBlur={() => finishediting()}
                />
            </TableCell>
        );
    } else {
        return (
            <TableCell {...props} onClick={() => setEditing(true)}>
                <Checkbox checked={checked} disabled />
            </TableCell>
        );
    }
}

const StatusTableCell = (props) => {
    const [selected, setSelected] = React.useState(false);
    const [data, setData] = React.useState(props.data);
    const updateValue = (newData) => {
        var params = {};
        params['Status'] = newData;
        postApi('/SalesOrder/UpdateField/' + props.id, params);
        setData(newData);
        props.changeStatus(newData);
    };

    if (selected) {
        return <TableCell {...props} style={{ maxWidth: '150px' }} >
            <SimpleStringSelect width={150} state={data} setState={updateValue} options={SOstatusList} default={0} autoFocus />
        </TableCell>
    } else {
        return <TableCell {...props} onClick={() => { setSelected(true) }} >{props.data}</TableCell>
    }
}
